import styled from '@emotion/styled'
import React from 'react'
import { Hugeicon } from '../../../icons/Hugeicon'

export type Plan = 'monthly' | 'yearly'

type Props = {
  value: Plan | null
  onChange: (plan: Plan) => void
}

const PlanSelector: React.FC<Props> = ({ value, onChange }) => {
  // Pre-select yearly plan on first render if no plan is selected
  React.useEffect(() => {
    if (value === null) {
      onChange('yearly')
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Container>
      <Title>Choose your plan</Title>
      <Description>
        Post your events once &mdash; they appear on your website and across the CivicLift community calendar network,
        reaching more people without extra work.
      </Description>

      <CardGrid>
        <Card selected={value === 'monthly'} onClick={() => onChange('monthly')}>
          <PlanName>Monthly</PlanName>
          <PriceContainer>
            <Price>$25</Price>
            <PricePeriod>/ month</PricePeriod>
          </PriceContainer>
          <FeatureList>
            <Feature>
              <CheckIcon />
              Embed calendar on your website
            </Feature>
            <Feature>
              <CheckIcon />
              Events flow to CivicLift network
            </Feature>
            <Feature>
              <CheckIcon />
              Monthly analytics reports
            </Feature>
            <Feature>
              <CheckIcon />
              14-day free trial
            </Feature>
          </FeatureList>
          {value === 'monthly' && <SelectedBadge>Selected</SelectedBadge>}
        </Card>

        <Card selected={value === 'yearly'} onClick={() => onChange('yearly')} recommended>
          <RecommendedBadge>Best Value</RecommendedBadge>
          <PlanName>Yearly</PlanName>
          <PriceContainer>
            <Price>$250</Price>
            <PricePeriod>/ year</PricePeriod>
          </PriceContainer>
          <Savings>Save $50 (17% off)</Savings>
          <FeatureList>
            <Feature>
              <CheckIcon />
              Everything in Monthly
            </Feature>
            <Feature>
              <CheckIcon />
              2 months free
            </Feature>
            <Feature>
              <CheckIcon />
              Priority support
            </Feature>
            <Feature>
              <CheckIcon />
              14-day free trial
            </Feature>
          </FeatureList>
          {value === 'yearly' && <SelectedBadge>Selected</SelectedBadge>}
        </Card>
      </CardGrid>

      <TrialNote>Start with a 14-day free trial. Cancel anytime.</TrialNote>
    </Container>
  )
}

export default PlanSelector

const CheckIcon: React.FC = () => (
  <Hugeicon name="tick" size={16} />
)

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #102638;
  margin: 0 0 8px;
  text-align: center;
`

const Description = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 32px;
  text-align: center;
`

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

const Card = styled.div<{ selected: boolean; recommended?: boolean }>`
  position: relative;
  background: ${props => (props.selected ? '#f0f7ff' : '#ffffff')};
  border: 2px solid ${props => (props.selected ? '#3b82f6' : props.recommended ? '#22c55e' : '#e5e7eb')};
  border-radius: 12px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => (props.selected ? '#3b82f6' : props.recommended ? '#22c55e' : '#9ca3af')};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`

const RecommendedBadge = styled.span`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 100px;
  white-space: nowrap;
`

const PlanName = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #102638;
  margin: 8px 0 16px;
`

const PriceContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 8px;
`

const Price = styled.span`
  font-size: 36px;
  font-weight: 700;
  color: #102638;
`

const PricePeriod = styled.span`
  font-size: 16px;
  color: #6b7280;
`

const Savings = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #22c55e;
  margin-bottom: 16px;
`

const FeatureList = styled.ul`
  list-style: none;
  margin: 16px 0 0;
  padding: 0;
`

const Feature = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #4b5563;
  margin-bottom: 12px;

  svg {
    color: #22c55e;
    flex-shrink: 0;
  }
`

const SelectedBadge = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  background: #3b82f6;
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 100px;
`

const TrialNote = styled.p`
  text-align: center;
  font-size: 14px;
  color: #6b7280;
  margin-top: 24px;
`
